import { driver } from "driver.js";
import "driver.js/dist/driver.css";

/**
 * Dashboard Options Tour (Steps 8-9)
 * Shows after game tutorial carousel completes
 * Highlights: Progress tracking and Chat with Mendy
 * @param {Function} onComplete - Callback function to run after tour completes
 */
export const createDashboardOptionsTour = (onComplete) => {
  const driverObj = driver({
    showProgress: false, // Disable default progress, we'll add custom per step
    showButtons: ['next', 'previous', 'close'],
    
    // Styling to match therapeutic app theme
    popoverClass: 'movemend-tour-popover',
    
    // Touch-friendly: Easy to dismiss but not by accident
    allowClose: true,
    overlayClickNext: false,
    
    // Smooth animations
    animate: true,
    
    // Disable keyboard navigation to prevent accidental dismissal on tablets
    disableActiveInteraction: false,
    
    // Steps for the dashboard options tour (steps 8-9)
    steps: [
      {
        element: '.dashboard-progress-option', // Progress option
        popover: {
          title: 'Understand Your Progress',
          description: 'Tap here to see an overview of all you\'ve accomplished so far. Track your exercise history and improvements.',
          side: 'bottom',
          align: 'center',
          // Custom progress for step 8
          showProgress: true,
          progressText: '8 of 9'
        }
      },
      {
        element: '.dashboard-chat-option', // Chat with Mendy option
        popover: {
          title: 'Chat with Mendy',
          description: 'Tap here to chat with Mendy, your AI rehab coach. Mendy can answer questions and provide encouragement anytime you need it.',
          side: 'bottom',
          align: 'center',
          // Custom progress for step 9
          showProgress: true,
          progressText: '9 of 9'
        }
      }
    ],
    
    // Callbacks
    onDestroyStarted: () => {
      // Mark tour as completed for this patient
      const patientId = localStorage.getItem('patientId');
      if (patientId) {
        localStorage.setItem(`dashboard_options_tour_completed_${patientId}`, 'true');
      }
      driverObj.destroy();
      
      // Call the completion callback if provided
      if (onComplete && typeof onComplete === 'function') {
        setTimeout(() => {
          onComplete();
        }, 100);
      }
    }
  });

  return driverObj;
};

/**
 * Check if current patient should see the tour
 * Only shows once per patient (after carousel completes)
 */
export const shouldShowDashboardOptionsTour = () => {
  const patientId = localStorage.getItem('patientId');
  if (!patientId) return false;
  
  const hasCompletedTour = localStorage.getItem(`dashboard_options_tour_completed_${patientId}`);
  return !hasCompletedTour;
};

/**
 * Start the dashboard options tour
 * Shows only for first-time patients after game carousel
 * @param {Function} onComplete - Callback function to run after tour completes
 */
export const startDashboardOptionsTour = (onComplete) => {
  if (shouldShowDashboardOptionsTour()) {
    const tour = createDashboardOptionsTour(onComplete);
    // Small delay to ensure carousel is closed
    setTimeout(() => {
      tour.drive();
    }, 500);
  } else if (onComplete && typeof onComplete === 'function') {
    // If tour already seen, call callback immediately
    onComplete();
  }
};

/**
 * Reset tour for current patient (for testing)
 */
export const resetDashboardOptionsTour = () => {
  const patientId = localStorage.getItem('patientId');
  if (patientId) {
    localStorage.removeItem(`dashboard_options_tour_completed_${patientId}`);
  }
};
